import { useEffect, useRef, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X } from "lucide-react";
import { BrynLogo } from "@/components/BrynLogo";
import { DESC } from "@/lib/publicContent";

export const SIGNUP = "/login?mode=register";
export const NAV = [["/features", "Features"], ["/how-it-works", "How it works"], ["/pricing", "Pricing"], ["/about", "About"]];

export const TrustLine = ({ className = "", testid = "trust-line" }) => <p className={`text-xs text-neutral-500 ${className}`} data-testid={testid}>Free tier. No credit card required. Cancel anytime.</p>;
export const PrivacyLine = () => (
  <p className="text-xs text-neutral-500" data-testid="privacy-line">We never sell your data. Your private memory stays yours. <Link to="/privacy" className="text-sky-400 hover:underline">Privacy</Link></p>
);

export const Cta = ({ children = "Create free account", testid = "cta-signup", className = "" }) => (
  <Link to={SIGNUP} data-testid={testid}
    className={`inline-flex items-center justify-center rounded-full bg-sky-400 text-white px-6 py-3 text-sm font-medium shadow-[0_10px_30px_-12px_rgba(37,99,255,0.9)] hover:bg-sky-500 hover:-translate-y-px ${className}`}>
    {children}
  </Link>
);

const testidOf = (label) => label.toLowerCase().replace(/ /g, "-");

function MobileMenu({ onClose }) {
  return (
    <div id="public-mobile-menu" className="md:hidden border-t border-white/[0.06] bg-[#0B0E17]/95 backdrop-blur-xl px-5 pt-3 pb-6" data-testid="public-mobile-menu">
      <nav aria-label="Main" className="flex flex-col">
        {NAV.map(([to, l]) => (
          <NavLink key={to} to={to} onClick={onClose} data-testid={`mobile-nav-${testidOf(l)}`}
            className={({ isActive }) => `py-3 text-base border-b border-white/[0.05] ${isActive ? "text-white" : "text-neutral-300 hover:text-white"}`}>{l}</NavLink>
        ))}
      </nav>
      <div className="mt-5 flex flex-col gap-3">
        <Link to="/login?mode=login" onClick={onClose} className="text-center py-3 rounded-full border border-white/15 text-sm text-neutral-200 hover:bg-white/5" data-testid="mobile-login-link">Log in</Link>
        <Cta testid="mobile-signup-btn" className="w-full">Create free account</Cta>
      </div>
    </div>
  );
}

function TopNav() {
  const [open, setOpen] = useState(false);
  const btn = useRef(null);
  const { pathname } = useLocation();
  useEffect(() => { setOpen(false); }, [pathname]);
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key !== "Escape") return;
      setOpen(false);
      btn.current?.focus();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open]);
  return (
    <header className="sticky top-0 z-40 border-b border-white/[0.06] bg-[#0B0E17]/60 backdrop-blur-xl backdrop-saturate-150" data-testid="public-nav">
      <div className="max-w-7xl mx-auto px-5 sm:px-8 h-14 flex items-center gap-8">
        <Link to="/" className="flex items-center rounded-lg" data-testid="public-logo" aria-label="Bryn AI home"><BrynLogo markClass="h-7 w-7" text="text-base" /></Link>
        <nav aria-label="Main" className="hidden md:flex items-center gap-7">
          {NAV.map(([to, l]) => (
            <NavLink key={to} to={to} data-testid={`public-nav-${testidOf(l)}`}
              className={({ isActive }) => `text-[13px] ${isActive ? "text-white" : "text-neutral-400 hover:text-white"}`}>{l}</NavLink>
          ))}
        </nav>
        <div className="ml-auto hidden md:flex items-center gap-5">
          <Link to="/login?mode=login" className="text-[13px] text-neutral-300 hover:text-white" data-testid="public-login-link">Log in</Link>
          <Cta testid="nav-signup-btn" className="px-4 py-1.5 text-[13px]">Create free account</Cta>
        </div>
        <button ref={btn} type="button" className="ml-auto md:hidden h-10 w-10 -mr-2 flex items-center justify-center rounded-full text-neutral-200 hover:bg-white/[0.06]"
          onClick={() => setOpen((o) => !o)} aria-expanded={open} aria-controls="public-mobile-menu" aria-label={open ? "Close menu" : "Open menu"} data-testid="public-menu-btn">
          {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
        </button>
      </div>
      {open && <MobileMenu onClose={() => setOpen(false)} />}
    </header>
  );
}

function Footer() {
  return (
    <footer className="border-t border-white/[0.06] mt-28 sm:mt-36" data-testid="public-footer">
      <div className="max-w-7xl mx-auto px-5 sm:px-8 py-12 flex flex-col md:flex-row md:items-start justify-between gap-8">
        <div>
          <Link to="/" className="inline-flex rounded-lg" aria-label="Bryn AI home" data-testid="footer-logo"><BrynLogo markClass="h-6 w-6" text="text-sm" /></Link>
          <p className="text-sm text-neutral-500 mt-3">AI study tutor that explains, step by step.</p>
        </div>
        <nav aria-label="Footer" className="flex flex-wrap gap-x-7 gap-y-3 text-sm">
          {[...NAV, ["/privacy", "Privacy"]].map(([to, l]) => (
            <Link key={to} to={to} className="text-neutral-400 hover:text-white" data-testid={`footer-link-${testidOf(l)}`}>{l}</Link>
          ))}
        </nav>
      </div>
      <div className="max-w-7xl mx-auto px-5 sm:px-8 pb-10 flex flex-wrap justify-between gap-3 text-xs text-neutral-500">
        <span data-testid="footer-stripe-note">Payments handled securely by Stripe.</span>
        <span data-testid="footer-privacy-note">We never sell your data.</span>
      </div>
    </footer>
  );
}

function usePage(title) {
  useEffect(() => {
    document.title = title ? `${title} · BrynAI` : "BrynAI — AI study tutor that explains, step by step";
    document.querySelector('meta[name="description"]')?.setAttribute("content", DESC);
  }, [title]);
}

export function Shell({ title, children }) {
  usePage(title);
  return (
    <div className="min-h-screen relative z-10 bg-[#0B0E17] text-white">
      <a href="#main" data-testid="skip-to-content"
        className="sr-only focus:not-sr-only focus:fixed focus:left-4 focus:top-3 focus:z-[60] focus:rounded-full focus:bg-sky-400 focus:px-4 focus:py-2 focus:text-sm focus:text-white">
        Skip to content
      </a>
      <TopNav />
      <main id="main" tabIndex={-1} className="outline-none">{children}</main>
      <Footer />
    </div>
  );
}
